import type { z } from "zod";
import type { JsonValue } from "@wags/utils/json.ts";
import { createServerFn } from "@tanstack/react-start";
import { kitsWidgetState } from "@wags/db/schema";
import { eq, sql } from "drizzle-orm";
import { useSyncExternalStore } from "react";
import { requireViewer, resolveViewerProfileIdentity } from "@/lib/core/auth";
import { getDb } from "@/lib/core/db.server";
import {
    getKitsWidgetStatesInputSchema,
    kitsWidgetStateSchema,
    saveKitsWidgetStateInputSchema,
} from "./widget-state.contract.ts";

const getWidgetStates = createServerFn({ method: "GET" })
    .inputValidator(getKitsWidgetStatesInputSchema)
    .handler(async ({ data }): Promise<Record<string, JsonValue>> => {
        const viewer = await resolveViewerProfileIdentity();
        if (!viewer) return {};
        const db = await getDb();
        const { findWebKitsConversationAccessForUser } =
            await import("../domain/web-conversation-access.ts");
        const access = await findWebKitsConversationAccessForUser(db, {
            conversationId: data.conversationId,
            userProfileId: viewer.appUserId,
        });
        if (!access) return {};
        const rows = await db.query.kitsWidgetState.findMany({
            where: eq(kitsWidgetState.conversationId, access.conversation.id),
        });
        return Object.fromEntries(
            rows.flatMap((row) => {
                const parsed = kitsWidgetStateSchema.safeParse(row.state);
                return parsed.success ? [[row.toolCallId, parsed.data]] : [];
            }),
        );
    });

export const saveWidgetState = createServerFn({ method: "POST" })
    .inputValidator(saveKitsWidgetStateInputSchema)
    .handler(async ({ data }) => {
        const viewer = await resolveViewerProfileIdentity();
        const resolvedViewer = requireViewer(viewer);
        const db = await getDb();
        const { findWebKitsConversationAccessForUser } =
            await import("../domain/web-conversation-access.ts");
        const access = await findWebKitsConversationAccessForUser(db, {
            conversationId: data.conversationId,
            userProfileId: resolvedViewer.appUserId,
        });
        if (!access) return { ok: false };
        await db
            .insert(kitsWidgetState)
            .values({
                conversationId: access.conversation.id,
                state: data.state,
                toolCallId: data.toolCallId,
            })
            .onConflictDoUpdate({
                set: { state: data.state, updatedAt: sql`now()` },
                target: [
                    kitsWidgetState.conversationId,
                    kitsWidgetState.toolCallId,
                ],
            });
        return { ok: true };
    });

const conversationStates = new Map<string, Map<string, JsonValue>>();
const subscribers = new Set<() => void>();
const hydrations = new Map<string, Promise<void>>();
const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();

const SAVE_DEBOUNCE_MS = 800;

function emit() {
    for (const cb of subscribers) cb();
}

function subscribe(cb: () => void) {
    subscribers.add(cb);
    return () => {
        subscribers.delete(cb);
    };
}

function statesFor(conversationId: string) {
    let states = conversationStates.get(conversationId);
    if (!states) {
        states = new Map();
        conversationStates.set(conversationId, states);
    }
    return states;
}

export function hydrateWidgetStates(conversationId: string): Promise<void> {
    let hydration = hydrations.get(conversationId);
    if (!hydration) {
        hydration = getWidgetStates({ data: { conversationId } })
            .then((rows) => {
                const states = statesFor(conversationId);
                for (const [toolCallId, state] of Object.entries(rows)) {
                    if (!states.has(toolCallId)) {
                        states.set(toolCallId, state);
                    }
                }
                emit();
            })
            .catch(() => {});
        hydrations.set(conversationId, hydration);
    }
    return hydration;
}

function scheduleSave(
    conversationId: string,
    toolCallId: string,
    state: JsonValue,
) {
    const key = `${conversationId}:${toolCallId}`;
    const existing = saveTimers.get(key);
    if (existing) clearTimeout(existing);
    saveTimers.set(
        key,
        setTimeout(() => {
            saveTimers.delete(key);
            const parsed = kitsWidgetStateSchema.safeParse(state);
            if (!parsed.success) return;
            saveWidgetState({
                data: {
                    conversationId,
                    state: parsed.data,
                    toolCallId,
                },
            }).catch(() => {});
        }, SAVE_DEBOUNCE_MS),
    );
}

export function useWidgetState<S extends JsonValue>(
    conversationId: string | undefined,
    toolCallId: string,
    initial: S,
    schema: z.ZodType<S>,
): [S, (next: S) => void] {
    const stored = useSyncExternalStore(
        subscribe,
        () =>
            conversationId
                ? conversationStates.get(conversationId)?.get(toolCallId)
                : undefined,
        () => undefined,
    );
    const set = (next: S) => {
        if (!conversationId) return;
        statesFor(conversationId).set(toolCallId, next);
        emit();
        scheduleSave(conversationId, toolCallId, next);
    };
    const parsed = schema.safeParse(stored);
    return [parsed.success ? parsed.data : initial, set];
}
